import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Calendar, ChevronRight } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export default function AccountantMainDashboard({ setSelectedEventId }) {
  const [events, setEvents] = useState([]);
  const [eventSummaries, setEventSummaries] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { authAxios } = useAuth();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true); setError(null);
    try {
      const [evtRes, anaRes] = await Promise.all([authAxios.get("/events"), authAxios.get("/analytics/accountant/all-events")]);
      setEvents(evtRes.data); setEventSummaries(anaRes.data.event_payout_summaries || {});
    } catch (e) { setError(e.response?.data?.detail || "Failed to load data"); toast.error(e.response?.data?.detail || "Failed to load data"); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Loading...</div>;
  if (error) return (<div className="text-center py-8"><p className="text-red-500 mb-4">{error}</p><button onClick={fetchData} className="px-4 py-2 bg-[#1e3a5f] text-white rounded-full">Retry</button></div>);

  return (
    <>
      <div className="mb-4"><h2 className="font-bold text-lg text-[#1e3a5f]">Your Events</h2><p className="text-slate-500 text-sm">Select an event to manage wallet top-ups</p></div>
      {events.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 text-center shadow-sm"><Calendar className="w-12 h-12 text-slate-300 mx-auto mb-2" /><p className="text-slate-500">No events assigned to you yet</p></div>
      ) : (
        <div className="space-y-3">
          {events.map(e => {
            const ps = eventSummaries[e.id];
            const isUpcoming = e.status === "upcoming";
            return (
              <div key={e.id} onClick={isUpcoming ? undefined : () => setSelectedEventId(e.id)} className={`w-full bg-white rounded-2xl p-4 shadow-sm text-left ${isUpcoming ? "opacity-60" : "hover:shadow-md transition-shadow cursor-pointer"}`} data-testid={`event-${e.id}`}>
                <div className="flex justify-between items-center mb-2">
                  <div><h3 className="font-bold text-[#1e3a5f]">{e.name}</h3><span className={`text-xs px-2 py-1 rounded-full ${e.status === "active" ? "bg-green-100 text-green-600" : e.status === "upcoming" ? "bg-amber-100 text-amber-600" : "bg-slate-100 text-slate-400"}`}>{e.status?.toUpperCase()}</span></div>
                  {!isUpcoming && <ChevronRight className="w-5 h-5 text-slate-400" />}
                </div>
                <p className="text-slate-400 text-sm mb-2">{e.start_date} - {e.end_date}</p>
                <div className="grid grid-cols-3 gap-2 pt-2 border-t text-xs">
                  <div><span className="text-slate-500">Sales</span><p className="font-bold text-[#1e3a5f]">{"\u20ac"}{(ps?.sales || 0).toFixed(2)}</p></div>
                  <div><span className="text-slate-500">Payout</span><p className="font-bold text-green-600">{"\u20ac"}{(ps?.total_payout || 0).toFixed(2)}</p></div>
                  <div><span className="text-slate-500">Pending</span><p className="font-bold text-red-500">{"\u20ac"}{(ps?.pending_payout || 0).toFixed(2)}</p></div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
